import { useState, useEffect, useRef, useCallback } from 'react';
import { rpc } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { Pencil } from 'lucide-react';
import { PropagationDialog } from './PropagationDialog';
import {
  SetupForm,
  SETUP_KEYS,
  isSetupEmpty,
  type SetupData,
  type InventoryItem,
  type SetupInventory,
} from '@/components/setup/SetupForm';

interface Props {
  sessionId: string;
  sessionDate?: string;
  trackName?: string;
  driverName?: string;
}

function snapshotValues(setupData: SetupData): Record<string, string> {
  const orig: Record<string, string> = {};
  for (const key of SETUP_KEYS) {
    orig[key] = String((setupData as Record<string, unknown>)[key] ?? '');
  }
  orig.loosenedRearBumper = String(
    setupData.loosenedRearBumper === true || setupData.loosenedRearBumper === 'true'
  );
  return orig;
}

export function SessionSetupView({ sessionId }: Props) {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [setup, setSetup] = useState<SetupData | null>(null);
  const [data, setData] = useState<SetupData>({});
  const [writable, setWritable] = useState(false);
  const [readOnly, setReadOnly] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  const [inventory, setInventory] = useState<SetupInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const originalValuesRef = useRef<Record<string, string>>({});

  const [propagation, setPropagation] = useState<{
    candidates: Record<string, unknown>[];
    matchFields: Record<string, string>;
    newSetup: SetupData;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setReadOnly(true);
    setPropagation(null);
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [setupData, chassis, engines, tyres, carbs, axlesData, hubs, sparkPlugs] = await Promise.all([
          rpc<SetupData & { writable?: boolean }>('GetSessionKartSetup', { sessionId }),
          rpc<InventoryItem[]>('ListChassis'),
          rpc<InventoryItem[]>('ListEngines'),
          rpc<InventoryItem[]>('ListTyres'),
          rpc<InventoryItem[]>('ListCarburetors'),
          rpc<InventoryItem[]>('ListAxles'),
          rpc<InventoryItem[]>('ListHubs'),
          rpc<InventoryItem[]>('ListSparkPlugs'),
        ]);
        if (cancelled) return;
        setWritable(!!setupData.writable);
        setSetup(setupData);
        setData({ ...setupData });
        originalValuesRef.current = snapshotValues(setupData);
        setInventory({
          chassis: chassis || [],
          engines: engines || [],
          tyres: tyres || [],
          carburetors: carbs || [],
          axles: axlesData || [],
          hubs: hubs || [],
          sparkPlugs: sparkPlugs || [],
        });
      } catch (e: any) {
        if (!cancelled) setError(e.message || t('setup.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [sessionId, t, version]);

  const update = useCallback((partial: Partial<SetupData>) => {
    setData(prev => ({ ...prev, ...partial }));
  }, []);

  const handleStartEdit = useCallback(() => {
    setData({ ...(setup ?? {}) });
    setReadOnly(false);
  }, [setup]);

  const handleCancel = useCallback(() => {
    setData({ ...(setup ?? {}) });
    setReadOnly(true);
  }, [setup]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await rpc('UpsertSessionKartSetup', {
        sessionId,
        ...data,
      } as unknown as Record<string, unknown>);
      showToast(t('setup.saved'));

      // Compute diff for propagation before updating baseline snapshot
      const matchFields: Record<string, string> = {};
      const orig = originalValuesRef.current;
      for (const key of Object.keys(orig)) {
        const newVal = key === 'loosenedRearBumper'
          ? String(data.loosenedRearBumper === true || data.loosenedRearBumper === 'true')
          : String((data as Record<string, unknown>)[key] ?? '');
        if (newVal !== orig[key]) {
          matchFields[key] = orig[key];
        }
      }

      setSetup({ ...data });
      originalValuesRef.current = snapshotValues(data);
      setReadOnly(true);

      if (Object.keys(matchFields).length > 0) {
        try {
          const candidates = await rpc<Record<string, unknown>[]>('FindPropagationCandidates', {
            sessionId,
            matchFields,
          });
          if (candidates && candidates.length > 0) {
            setPropagation({ candidates, matchFields, newSetup: { ...data } });
          }
        } catch {
          // Non-fatal — skip propagation
        }
      }
    } catch {
      showToast(t('setup.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} />;
  if (!setup || !inventory) return <div className="text-text-muted text-sm">{t('setup.noSetup')}</div>;
  if (isSetupEmpty(setup) && !writable) return <div className="text-text-muted text-sm">{t('setup.noSetup')}</div>;

  return (
    <>
      {writable && (
        <div className="flex justify-end gap-2 mb-4">
          {readOnly ? (
            <button
              type="button"
              onClick={handleStartEdit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white
                hover:bg-accent-hover transition-colors cursor-pointer"
            >
              <Pencil size={12} />
              {t('setup.edit')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white
                  hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer"
              >
                {saving ? t('setup.saving') : t('setup.save')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary
                  border border-border-default hover:border-border-hover hover:text-text-primary disabled:opacity-50 transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
            </>
          )}
        </div>
      )}

      <SetupForm
        data={readOnly ? setup : data}
        inventory={inventory}
        readOnly={readOnly || !writable}
        onChange={update}
        onInventoryUpdate={setInventory}
      />

      {propagation && (
        <PropagationDialog
          candidates={propagation.candidates as any}
          matchFields={propagation.matchFields}
          newSetup={propagation.newSetup}
          inventory={inventory}
          onClose={() => {
            setPropagation(null);
            setVersion(v => v + 1);
          }}
        />
      )}
    </>
  );
}
